/**
 * Runtime i18n key-usage tracker + show-keys mode.
 *
 * Hooks into the i18next instance and exposes two independent debug helpers
 * that share a single postProcessor:
 *
 *   1. **Usage tracker** — records every translation key actually rendered
 *      (post `t()` resolution) and every key reported missing. Inspect via
 *      `window.__i18nUsage` (see API below).
 *   2. **Show-keys mode** — when enabled, replaces the rendered translation
 *      with the literal `ns:key` so engineers can identify which key produced
 *      which UI element. Toggled via `window.__i18nShowKeys`.
 *
 * Both flags are independent — track without showing, show without tracking,
 * or both.
 *
 * **Tracker:**
 *   - URL query: `?pixTrackI18n=1` (or `?pixTrackI18n=0` to override a persisted on
 *     for a single page load — does not clear localStorage)
 *   - localStorage: `localStorage.setItem('i18n:track', '1')`
 *   - Programmatic: `__i18nUsage.enable()` / `__i18nUsage.disable()`
 *
 *   Inspect:
 *     __i18nUsage.list()         // every recorded usage entry
 *     __i18nUsage.keys()         // unique `ns:key` resolved at least once
 *     __i18nUsage.missing()      // unique `ns:key` ever reported missing
 *     __i18nUsage.exportJson()   // serializable snapshot
 *     __i18nUsage.reset()
 *
 *   Entries are keyed by `${lng}:${ns}:${key}` so a key resolved in `en` but
 *   missing in `ja` produces two distinct entries. Recording stops after
 *   1000 unique entries to bound memory.
 *
 * **Show-keys:**
 *   - URL query: `?pixShowI18nKeys=1` (or `?pixShowI18nKeys=0` to override a persisted on)
 *   - localStorage: `localStorage.setItem('i18n:show-keys', '1')`
 *   - Debug Panel: "Show i18n Keys" Switch
 *
 *   Toggling at runtime forces an i18next `languageChanged` emit so already-
 *   rendered react-i18next consumers re-evaluate `t()` immediately, no
 *   reload required.
 *
 * Both flags carry minimal overhead when disabled — a single branch check
 * per `t()` call.
 */

import type {
  i18n as I18nInstance,
  PostProcessorModule,
  TOptions,
} from 'i18next'

interface UsageEntry {
  /** `${lng}:${ns}:${key}` — primary identity */
  id: string
  lng: string
  ns: string
  key: string
  /** True when i18next reported this (lng, ns, key) tuple as missing. */
  missing: boolean
  firstSeen: number
  lastSeen: number
  count: number
}

const STORAGE_FLAG_TRACK = 'i18n:track'
const QUERY_FLAG_TRACK = 'pixTrackI18n'
const STORAGE_FLAG_SHOW_KEYS = 'i18n:show-keys'
const QUERY_FLAG_SHOW_KEYS = 'pixShowI18nKeys'

/**
 * Hard cap on unique `(lng, ns, key)` entries. Once reached, new keys are
 * dropped (existing entries still get count/lastSeen bumps so the snapshot
 * stays accurate for what was captured). Bounds memory in long-lived
 * sessions; a warning is logged once per `reset()`.
 */
const MAX_ENTRIES = 1000

let enabled = false
let showKeys = false
let installed = false
let capWarned = false
let i18nRef: I18nInstance | null = null
const entries = new Map<string, UsageEntry>()

function readFlag(storageKey: string, queryKey: string): boolean {
  if (typeof window === 'undefined') return false
  try {
    // URL query param wins when present — `=1` enables, `=0` explicitly
    // disables (overriding any persisted localStorage value for this load).
    // Any other value falls through to the localStorage check.
    const params = new URLSearchParams(window.location.search)
    const qv = params.get(queryKey)
    if (qv === '1') return true
    if (qv === '0') return false
    if (window.localStorage?.getItem(storageKey) === '1') return true
  } catch {
    // ignore (e.g. SSR, sandboxed iframes without storage access)
  }
  return false
}

function writeFlag(storageKey: string, value: boolean) {
  if (typeof window === 'undefined') return
  try {
    if (value) window.localStorage?.setItem(storageKey, '1')
    else window.localStorage?.removeItem(storageKey)
  } catch {
    // ignore (sandboxed iframes, storage quota, etc.)
  }
}

/**
 * Force already-rendered react-i18next consumers to re-evaluate `t()` so a
 * show-keys toggle takes effect without a reload. The client init binds
 * react-i18next re-renders to `languageChanged`, so we re-emit it.
 *
 * `emit` is on the EventEmitter base class i18next extends, but it is not in
 * the public `i18next` d.ts. It has been stable across versions, but we still
 * guard the call so a future i18next change can't crash the toggle — show-keys
 * will simply require a reload to take effect.
 */
function forceI18nRerender() {
  if (!i18nRef) return
  const emitter = i18nRef as unknown as {
    emit?: (event: string, ...args: unknown[]) => void
  }
  try {
    emitter.emit?.('languageChanged', i18nRef.resolvedLanguage)
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      '[pixI18nDebug] failed to emit languageChanged for show-keys toggle; reload to apply.',
      err,
    )
  }
}

function splitNsKey(rawKey: string, fallbackNs: string): [string, string] {
  // i18next supports `ns:key` syntax; if no colon, the configured namespace
  // (provided via options) wins.
  const idx = rawKey.indexOf(':')
  if (idx === -1) return [fallbackNs, rawKey]
  return [rawKey.slice(0, idx), rawKey.slice(idx + 1)]
}

function record(
  rawKey: string,
  resolvedLng: string,
  optionsNs: string,
  missing: boolean,
) {
  if (!enabled) return
  const [ns, key] = splitNsKey(rawKey, optionsNs)
  const lng = resolvedLng || 'unknown'
  const id = `${lng}:${ns}:${key}`
  const now = Date.now()
  const existing = entries.get(id)
  if (existing) {
    existing.lastSeen = now
    existing.count += 1
    if (missing) existing.missing = true
    return
  }
  if (entries.size >= MAX_ENTRIES) {
    if (!capWarned) {
      capWarned = true
      // eslint-disable-next-line no-console
      console.warn(
        `[pixI18nDebug] reached ${MAX_ENTRIES} unique entries; new keys will be dropped. Call __i18nUsage.reset() to start over.`,
      )
    }
    return
  }
  entries.set(id, {
    id,
    lng,
    ns,
    key,
    missing,
    firstSeen: now,
    lastSeen: now,
    count: 1,
  })
}

function resolveNsFromOptions(options: TOptions | undefined): string {
  const ns = options?.ns
  if (Array.isArray(ns)) return ns.at(0) ?? 'common'
  return (ns as string | undefined) ?? 'common'
}

/** i18next postProcessor name — pass this in init({ postProcess: [...] }). */
export const I18N_DEBUG_POSTPROCESSOR_NAME = 'pixI18nDebug'

const i18nDebugPostProcessor: PostProcessorModule = {
  type: 'postProcessor',
  name: I18N_DEBUG_POSTPROCESSOR_NAME,
  process(value, key, options, translator) {
    if (!enabled && !showKeys) return value
    // `key` may be a single string or an array of fallback candidates. The
    // postProcessor signature does not expose which candidate i18next
    // actually resolved, so we use the *primary* (first) candidate as a
    // proxy. Empty-string keys are not valid i18next keys either, so
    // `!usedKey` covers null/undefined and "".
    const usedKey = Array.isArray(key) ? key.at(0) : key
    if (!usedKey) return value
    const ns = resolveNsFromOptions(options)
    if (enabled) {
      // Use the translator's resolved language. This intentionally ignores
      // per-call `t(key, { lng: '...' })` overrides — rare in this codebase
      // and not worth the extra complexity to track separately.
      const resolvedLng: string =
        translator?.language ?? translator?.options?.lng ?? ''
      record(String(usedKey), resolvedLng, ns, /* missing */ false)
    }
    if (showKeys) {
      const [foundNs, foundKey] = splitNsKey(String(usedKey), ns)
      return `${foundNs}:${foundKey}`
    }
    return value
  },
}

/** Public shape of `window.__i18nUsage`; also re-declared on Window in app-env.d.ts. */
export interface I18nUsageApi {
  enabled: () => boolean
  enable: () => void
  disable: () => void
  list: () => UsageEntry[]
  /** Unique `ns:key` strings resolved successfully in at least one language. */
  keys: () => string[]
  /** Unique `ns:key` strings ever reported missing in at least one language. */
  missing: () => string[]
  exportJson: () => {
    capturedAt: string
    enabled: boolean
    total: number
    /** Set to the cap value when the tracker has stopped recording new keys; otherwise undefined. */
    cappedAt?: number
    entries: UsageEntry[]
  }
  reset: () => void
}

function uniqueNsKey(predicate: (e: UsageEntry) => boolean): string[] {
  const seen = new Set<string>()
  for (const e of entries.values()) {
    if (predicate(e)) seen.add(`${e.ns}:${e.key}`)
  }
  return [...seen].sort()
}

export const __i18nUsage: I18nUsageApi = {
  enabled: () => enabled,
  enable: () => {
    enabled = true
    writeFlag(STORAGE_FLAG_TRACK, true)
  },
  disable: () => {
    enabled = false
    writeFlag(STORAGE_FLAG_TRACK, false)
  },
  list: () => [...entries.values()].sort((a, b) => b.lastSeen - a.lastSeen),
  keys: () => uniqueNsKey(e => !e.missing),
  missing: () => uniqueNsKey(e => e.missing),
  exportJson: () => ({
    capturedAt: new Date().toISOString(),
    enabled,
    total: entries.size,
    cappedAt: entries.size >= MAX_ENTRIES ? MAX_ENTRIES : undefined,
    entries: [...entries.values()],
  }),
  reset: () => {
    entries.clear()
    capWarned = false
  },
}

/**
 * Internal getters/setters for the show-keys flag. Used by the Debug Panel
 * toggle. There is no public programmatic / `window` API — toggling is via
 * the Debug Panel, the `?pixShowI18nKeys=1` URL flag, or the
 * `localStorage['i18n:show-keys']` key.
 */
export function isShowKeysEnabled(): boolean {
  return showKeys
}

export function setShowKeysEnabled(value: boolean): void {
  if (showKeys === value) return
  showKeys = value
  writeFlag(STORAGE_FLAG_SHOW_KEYS, value)
  forceI18nRerender()
}

/**
 * Wire the tracker + show-keys postProcessor into an i18next instance. Call
 * BEFORE `i18n.init(...)` so the module is registered when init resolves
 * keys. The caller is responsible for adding `I18N_DEBUG_POSTPROCESSOR_NAME`
 * to the `postProcess` option in init.
 *
 * Safe to call once. The postProcessor is always registered; the on-the-wire
 * cost when both flags are off is a single branch check per `t()` call.
 */
export function installI18nDebug(i18n: I18nInstance) {
  if (installed) {
    // eslint-disable-next-line no-console
    console.warn(
      '[pixI18nDebug] installI18nDebug called more than once; ignoring subsequent call.',
    )
    return
  }
  installed = true
  i18nRef = i18n

  i18n.use(i18nDebugPostProcessor)

  i18n.on('missingKey', (lngs, ns, key, _res) => {
    if (!enabled) return
    // i18next can pass `lngs` as either a single language code or the
    // fallback chain array — record one missing entry per attempted lang.
    const langs = Array.isArray(lngs) ? lngs : [lngs]
    for (const lng of langs) {
      record(String(key), String(lng ?? ''), String(ns ?? 'common'), true)
    }
  })

  enabled = readFlag(STORAGE_FLAG_TRACK, QUERY_FLAG_TRACK)
  showKeys = readFlag(STORAGE_FLAG_SHOW_KEYS, QUERY_FLAG_SHOW_KEYS)

  if (typeof window !== 'undefined') {
    window.__i18nUsage = __i18nUsage
  }
}
